import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Mail, Phone, Send, Loader2 } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/Reveal';
import { useToast } from '../hooks/use-toast';
import { CONTACT } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please complete the required fields', description: 'Name, email and message are required.' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: 'Message sent ✓', description: 'Thanks — the EIG team will be in touch shortly.' });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again, or email us directly.' });
    } finally {
      setSending(false);
    }
  };

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&output=embed`;

  return (
    <div>
      <PageBanner
        title="Get in touch"
        subtitle="Our headquarters are in the heart of Downtown Dubai, in the shadow of the Burj."
        crumb="Contact"
        image="https://images.unsplash.com/photo-1607414851776-f2fcc379fb48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxEdWJhaSUyMHNreWxpbmV8ZW58MHx8fHwxNzkwODQ5MjY0fDA&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="font-heading font-extrabold text-3xl text-[#132b4d] mb-4">Let’s start a conversation</h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              If you think we can help, just outline how below and we’ll be in touch. If you’re interested in an
              initial chat, please fill in the form.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#eaf6fc] text-[#1ca5dc] flex items-center justify-center shrink-0"><MapPin size={22} /></div>
                <div>
                  <h4 className="font-heading font-bold text-[#132b4d]">Mail</h4>
                  <p className="text-gray-600 text-sm mt-1">{CONTACT.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#eaf6fc] text-[#1ca5dc] flex items-center justify-center shrink-0"><Mail size={22} /></div>
                <div>
                  <h4 className="font-heading font-bold text-[#132b4d]">By Email</h4>
                  <a href={`mailto:${CONTACT.email}`} className="text-gray-600 text-sm mt-1 hover:text-[#1ca5dc] break-all">{CONTACT.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#eaf6fc] text-[#1ca5dc] flex items-center justify-center shrink-0"><Phone size={22} /></div>
                <div>
                  <h4 className="font-heading font-bold text-[#132b4d]">By Telephone</h4>
                  <a href={`tel:${CONTACT.phone.replace(/\s/g, '')}`} className="text-gray-600 text-sm mt-1 hover:text-[#1ca5dc]">{CONTACT.phone}</a>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden shadow-md border border-gray-100">
              <iframe
                title="EIG location"
                src={mapSrc}
                width="100%"
                height="260"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form onSubmit={handleSubmit} className="bg-[#f6f9fc] rounded-2xl p-8 md:p-10 border border-gray-100">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-heading font-semibold text-[#132b4d] mb-2">Your Name <span className="text-[#1ca5dc]">*</span></label>
                  <input name="name" value={form.name} onChange={handleChange} className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1ca5dc] focus:ring-2 focus:ring-[#1ca5dc]/20 transition" placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="block text-sm font-heading font-semibold text-[#132b4d] mb-2">Your Email <span className="text-[#1ca5dc]">*</span></label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1ca5dc] focus:ring-2 focus:ring-[#1ca5dc]/20 transition" placeholder="jane@school.ae" />
                </div>
                <div>
                  <label className="block text-sm font-heading font-semibold text-[#132b4d] mb-2">Subject</label>
                  <input name="subject" value={form.subject} onChange={handleChange} className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1ca5dc] focus:ring-2 focus:ring-[#1ca5dc]/20 transition" placeholder="How can we help?" />
                </div>
                <div>
                  <label className="block text-sm font-heading font-semibold text-[#132b4d] mb-2">Your Message <span className="text-[#1ca5dc]">*</span></label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={5} className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1ca5dc] focus:ring-2 focus:ring-[#1ca5dc]/20 transition resize-none" placeholder="Tell us about your challenge…" />
                </div>
                <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-[#132b4d] transition-colors disabled:opacity-70">
                  {sending ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Send message <Send size={17} /></>}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
